import React, { useState } from "react";
import { FormikProps } from "formik";
import { z } from "zod";
import { ProjectDataSchema } from "./projectDataSchema";
import InputField from "../components/common/InputField";
import { useGetHoliday } from "../api/query/useGetHoliday";
import Loader from "../assest/loader.svg";
import { toast } from "react-toastify";

type Props = {
  formik: FormikProps<z.infer<typeof ProjectDataSchema>>;
};

function NonWorkingDays({ formik }: Props) {
  const [selectedOption, setSelectedOption] = useState<string>("");
  const useGetHolidayQuery = useGetHoliday();

  const parseCustomDate = (dateStr: string): Date | null => {
    const parts = dateStr.split(".");
    if (parts.length !== 3) return null;

    const [day, month, year] = parts.map(Number);
    if (!day || !month || !year) return null;

    return new Date(year, month - 1, day + 1); // Month is 0-based in JS Date
  };

  const loadSuggestedDays = async () => {
    try {
      const res = await useGetHolidayQuery.refetch();

      if (res.isError || !res.data?.data) {
        console.error("Refetch error:", res.error);
        throw (
          res.failureReason?.response?.data ??
          new Error("Failed to fetch holidays.")
        );
      }

      const data = res.data?.data;
      const updatedDays = [...formik.values.nonWorkingDays];

      data?.forEach((d) => {
        const parsedNewDate = parseCustomDate(d.date);

        if (!parsedNewDate || isNaN(parsedNewDate.getTime())) {
          console.error("Invalid date format from AI:", d.date);
          throw new Error("Invalid date format returned by AI.");
        }

        const formattedNewDate = parsedNewDate.toISOString().split("T")[0];

        const exists = updatedDays.some((s) => {
          return (
            new Date(s.date).toISOString().split("T")[0] === formattedNewDate
          );
        });

        if (exists) {
          updatedDays.forEach((s) => {
            if (
              new Date(s.date).toISOString().split("T")[0] === formattedNewDate
            ) {
              s.reason = d.description;
            }
          });
        } else {
          updatedDays.push({ date: parsedNewDate, reason: d.description });
        }
      });

      formik.setFieldValue("nonWorkingDays", updatedDays);
      toast.success("Jours fériés français appliqués !");
    } catch (err: any) {
      console.error("Error caught in loadSuggestedDays:", err);
      const error_message =
        typeof err === "string"
          ? err
          : err?.detail
          ? err?.detail
          : err?.message
          ? err?.message
          : "Oops! Une erreur s'est produite lors de la récupération des jours fériés.";
      toast.error(error_message, { autoClose: false });
    }
  };

  const readCSV = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const json = csvToJson(text);
      formik.setFieldValue("nonWorkingDays", json);
      toast.success("Calendrier importé depuis le CSV !");
    };
    reader.readAsText(file);
  };

  const csvToJson = (csvText: string): { date: string; reason: string }[] => {
    const rows = csvText
      .split("\n")
      .map((row) => row.trim())
      .filter((row) => row);
    return rows.slice(1).map((row) => {
      const values = row.replace(/"/g, "").split(";");
      return {
        date: values[0].split(".").reverse().join("-"),
        reason: values[1] || "",
      };
    });
  };

  return (
    <div className="flex flex-col gap-4 p-1 relative">
      {/* Loading overlay */}
      {(useGetHolidayQuery.isFetching || useGetHolidayQuery.isRefetching) && (
        <div className="absolute inset-0 z-50 bg-black/60 backdrop-blur-sm rounded-2xl flex flex-col justify-center items-center gap-2">
          <img
            className="w-12 h-12 animate-spin"
            alt="Chargement..."
            src={Loader}
          />
          <span className="text-xs font-mono text-purple-300">
            Calcul des jours fériés en cours...
          </span>
        </div>
      )}

      {/* Header matching mockup */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-emerald-400 text-base">📅</span>
          <h3 className="text-sm font-display font-semibold text-slate-900 dark:text-white">
            Jours non travaillés & Calendrier de travail
          </h3>
        </div>
        <span className="text-[11px] font-mono text-slate-400 dark:text-slate-500">
          Zone géographique : France (Métropole)
        </span>
      </div>

      {/* Radio Option Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Option 1: Suggested French Holidays */}
        <div
          onClick={() => {
            setSelectedOption("suggested");
            loadSuggestedDays();
          }}
          className={`p-4 rounded-2xl border cursor-pointer transition-all duration-200 ${
            selectedOption === "suggested"
              ? "bg-purple-500/10 border-purple-500/40 shadow-glow-purple"
              : "bg-white dark:bg-[#161924] border-slate-200 dark:border-white/10 hover:border-purple-500/30"
          }`}
        >
          <div className="flex items-start gap-3">
            <input
              type="radio"
              name="holiday_option"
              checked={selectedOption === "suggested"}
              onChange={() => {
                setSelectedOption("suggested");
                loadSuggestedDays();
              }}
              className="mt-1 accent-purple-600"
            />
            <div>
              <h4 className="text-xs font-semibold text-slate-900 dark:text-white">
                Liste suggérée des jours fériés français
              </h4>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                Fêtes, ponts & week-ends auto-détectés (Calcul dynamique)
              </p>
            </div>
          </div>
        </div>

        {/* Option 2: Upload CSV */}
        <div
          onClick={() => setSelectedOption("upload")}
          className={`p-4 rounded-2xl border cursor-pointer transition-all duration-200 ${
            selectedOption === "upload"
              ? "bg-purple-500/10 border-purple-500/40 shadow-glow-purple"
              : "bg-white dark:bg-[#161924] border-slate-200 dark:border-white/10 hover:border-purple-500/30"
          }`}
        >
          <div className="flex items-start gap-3">
            <input
              type="radio"
              name="holiday_option"
              checked={selectedOption === "upload"}
              onChange={() => setSelectedOption("upload")}
              className="mt-1 accent-purple-600"
            />
            <div>
              <h4 className="text-xs font-semibold text-slate-900 dark:text-white">
                Charger un fichier de congés (.CSV)
              </h4>
              <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                Importer calendrier d'entreprise ou RTT d'équipe personnalisés
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* File Upload Section */}
      {selectedOption === "upload" && (
        <div className="p-3 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 animate-fadeIn">
          <input
            type="file"
            className="w-full text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded-xl file:border-0 file:text-xs file:font-medium file:bg-purple-600 file:text-white hover:file:bg-purple-700"
            accept="text/csv"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                formik.setFieldValue("file", { file });
                readCSV(file);
              }
            }}
          />
          {formik.errors.file && (
            <span className="text-red-500 text-xs block mt-1">
              {formik.errors.file}
            </span>
          )}
        </div>
      )}

      {/* Non-working days listing */}
      <div className="mt-1">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] font-mono uppercase tracking-wider text-slate-400 dark:text-slate-500">
            Jours chômés configurés ({formik.values.nonWorkingDays.length}) :
          </span>
          <button
            type="button"
            className="px-2.5 py-1 rounded-lg text-xs font-medium text-purple-600 dark:text-purple-300 bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/20 transition-colors"
            onClick={() =>
              formik.setFieldValue("nonWorkingDays", [
                ...formik.values.nonWorkingDays,
                { date: "", reason: "" },
              ])
            }
          >
            + Ajouter manuellement
          </button>
        </div>

        {/* Existing days list */}
        <div className="max-h-56 overflow-y-auto space-y-2 pr-1">
          {formik.values.nonWorkingDays.map((day, index) => (
            <div
              key={index}
              className="flex items-center gap-2 p-2 rounded-xl bg-white dark:bg-[#161924] border border-slate-200 dark:border-white/10"
            >
              <InputField
                type="date"
                className="!w-36"
                name={`nonWorkingDays[${index}].date`}
                value={
                  day.date
                    ? new Date(day.date).toISOString().split("T")[0]
                    : undefined
                }
                onChange={(e) =>
                  formik.setFieldValue(
                    `nonWorkingDays[${index}].date`,
                    e.target.value
                      ? new Date(e.target.value).toISOString().split("T")[0]
                      : ""
                  )
                }
              />
              <InputField
                type="text"
                placeholder="Motif (ex: Fête du Travail)"
                className="flex-1"
                name={`nonWorkingDays[${index}].reason`}
                value={day.reason || ""}
                onChange={formik.handleChange}
              />
              <button
                type="button"
                className="p-1 text-slate-400 hover:text-red-500 transition-colors text-xs"
                onClick={() => {
                  const updatedDays = formik.values.nonWorkingDays.filter(
                    (_, i) => i !== index
                  );
                  formik.setFieldValue("nonWorkingDays", updatedDays);
                }}
                title="Supprimer"
              >
                ✖
              </button>
            </div>
          ))}

          {formik.values.nonWorkingDays.length === 0 && (
            <div className="text-center py-6 text-xs text-slate-400 border border-dashed border-slate-300 dark:border-white/10 rounded-xl">
              Aucun jour chômé sélectionné. Choisissez une option ci-dessus ou ajoutez-en manuellement.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default NonWorkingDays;
